import sortBy from 'lodash/sortBy';
import omit from 'lodash/omit';
import {tryParseDate} from "../../utils/DateTime";

export const crewListModes = {
  DISPLAY: 'display', // show crew lists
  SELECT: 'select', // select timeplans for a new crew change
  RESELECT: 're-select', // edit crew list
  STORE: 'store', // store crew list to display for form
  INACTIVE: 'inactive',
};

/**
 * getCrewListBySelectedTimeplans
 * @param {Object} selectedTsById
 * @param {Date} date
 * @returns {[{}]}
 */
export const getCrewListBySelectedTimeplans = (selectedTsById, dateString) => {
  const date = tryParseDate(dateString);
  const ts = Object
    .entries(selectedTsById)
    .map((i) => i[1])
    .map(({ start, end, msId, ...t }) => ({
      msId,
      start: new Date(start),
      end: new Date(end),
      ...t,
    }));

  const groupedTs = ts.reduce((acc, t) => ({
    ...acc,
    [t.msId]: [...(acc[t.msId] || []), t],
  }), {});

  // CREW-1271: which slot is leaving and which is arriving follows from the
  // slots themselves, not from the date.
  //
  // This used to compare each slot's MIDPOINT against the crew-change date. A
  // midpoint is a function of how long a contract is, not of who relieves whom:
  // two slots of one position run back to back (`a.end === b.start`), and that
  // join is what decides. So the old rule read a quantity unrelated to the
  // question and agreed with the right answer only by accident — a Sign ON date
  // anywhere in the first half of the leaving contract put both slots in the On
  // column under one key, and the second silently overwrote the first.
  //
  // Sorting by `start` says it directly: of the slots on a position, the earlier
  // one is signing off and the next one is signing on. Pairs beyond the first
  // become further rows rather than overwriting — `crew_lists` is a row per pair
  // of slots, not per position, and 37 of 6,910 production groups carry more
  // than one pair.
  const pairFor = (slots) => {
    const ordered = sortBy(slots, (t) => t.start.getTime());
    const pairs = [];

    for (let i = 0; i < ordered.length; i += 2) {
      pairs.push([ordered[i], ordered[i + 1]]);
    }

    return pairs;
  };

  const pairsByPosition = Object.values(groupedTs).map(pairFor);

  // A position with a single slot is the ordinary one-to-zero or zero-to-one
  // change — 2,078 of 6,963 production rows are one-sided — and there is no
  // second slot to give the order away, so it needs a date to compare against.
  //
  // The date box is usually still empty at this point, and there is a reference
  // available anyway: the handover that a paired position already describes,
  // which is where its second slot starts. Without it, every one-sided position
  // defaulted to "leaving" — on 17.09.2026 that listed five people who plainly
  // arrive on 01.11 as signing off, when the selection was one leaving and six
  // arriving.
  const handovers = pairsByPosition
    .flat()
    .filter(([, second]) => second)
    .map(([, second]) => second.start.getTime());

  // How far a moment sits from a slot — nothing if it falls inside it.
  const distanceFrom = (slot, at) => {
    const start = slot.start.getTime();
    const end = slot.end.getTime();

    if (at >= start && at <= end) return 0;

    return Math.min(Math.abs(at - start), Math.abs(at - end));
  };

  // Typed date first, then the handover nearest to this particular slot, and
  // today only as a last resort. That order is deliberate: the handover belongs
  // to this crew change, while today says nothing useful about one planned for
  // next March. Today still beats having no answer — a selection of one-sided
  // positions alone used to render an empty list until a date was typed.
  //
  // Nearest, not the average of all of them: the mean of two handovers is a date
  // on which nothing happens. Selections whose handovers differ are rare — 223 of
  // the 230 production crew changes with several pairs and a one-sided row put
  // every handover on the same day — but the other 7 spread them by up to 67
  // days, and the midpoint of that is two months from either real change.
  const referenceFor = (slot) => {
    if (date) return new Date(date).getTime();
    if (!handovers.length) return Date.now();

    return handovers.reduce((best, at) =>
      (distanceFrom(slot, at) < distanceFrom(slot, best) ? at : best));
  };

  // A slot that has ended by the reference is leaving, one that has not started
  // is arriving, and a reference inside the slot goes to the nearer edge.
  const isLeaving = (slot) => {
    const reference = referenceFor(slot);

    if (slot.end.getTime() <= reference) return true;
    if (slot.start.getTime() >= reference) return false;

    return (reference - slot.start.getTime()) > (slot.end.getTime() - reference);
  };

  const rowFor = ([first, second]) => {
    const row = {
      rank: first.role,
      rolePriority: first.rolePriority,
      roleCreatedAt: first.roleCreatedAt,
    };

    // `sign_*_status` is spelled the way the API serves a saved pair, so
    // `uncountedPositions` reads a row picked on the board and one read back
    // from the server alike (CREW-1252).
    const put = (slot, prefix) => {
      row[`${prefix}_id`] = slot.id;
      row[`${prefix}_seaman`] = slot.userName || slot.text;
      row[prefix === 'off_sign' ? 'sign_off_status' : 'sign_on_status'] = slot.status;
    };

    if (second) {
      put(first, 'off_sign');
      put(second, 'on_sign');
    } else {
      put(first, isLeaving(first) ? 'off_sign' : 'on_sign');
    }

    return row;
  };

  const rows = pairsByPosition.flat().map(rowFor);

  return sortBy(rows, ['rolePriority', 'roleCreatedAt']);
};

export const getIdsGroupedByTimeplanListType = (list) => list.reduce((acc, listItem) => {
  const onId = listItem.sign_on_id;
  const offId = listItem.sign_off_id;
  const hasReplace = onId && offId;

  if (hasReplace) return { ...acc, onWithOff: [...acc.onWithOff, onId], offWithOn: [...acc.offWithOn, offId] };
  if (onId) return { ...acc, onWithoutOff: [...acc.onWithoutOff, onId] };
  if (offId) return { ...acc, offWithoutOn: [...acc.offWithoutOn, offId] };

  return acc;
}, { offWithoutOn: [], onWithoutOff: [], onWithOff: [], offWithOn: [] });


export const setTimeplanToState = (state, timeplan) => {
  const oldData = state.data || {};

  const data = (
    oldData[timeplan.id]
      ? omit(oldData, timeplan.id)
      : { ...oldData, [timeplan.id]: timeplan }
  );

  return ({ ...state, data });
};
